import type {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteManyParams,
  DeleteManyResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  RaRecord,
  UpdateManyParams,
  UpdateManyResult,
  UpdateParams,
  UpdateResult,
} from 'react-admin';
import {getAccessTokenUseCase} from '@/compositionRoot';
import {API_URL} from '@/config';

const BASE_URL = API_URL?.replace(/\/$/, '') ?? '';
const API_PREFIX = '';

class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

const httpClient = async (url: string, options: RequestInit = {}) => {
  const token = await getAccessTokenUseCase.execute();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {...options, headers});

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return {data: undefined, status: response.status, headers: response.headers};
  }

  if (response.status >= 200 && response.status < 300) {
    const json = await response.json();
    return {data: json, status: response.status, headers: response.headers};
  }

  const text = await response.text();
  let payload: unknown;
  try {
    payload = JSON.parse(text);
  } catch {
    // ignore
  }
  throw new HttpError(text, response.status, payload);
};

const apiUrl = (resource: string, id?: string) => {
  const path = id ? `${resource}/${id}` : resource;
  return `${BASE_URL}${API_PREFIX}/${path}`;
};

const addId = <T extends Record<string, unknown>>(item: T): T & {id: string} => ({
  ...item,
  id: item.uuid as string,
});

const dataProvider: DataProvider = {
  // biome-ignore lint/suspicious/noExplicitAny: Required by react-admin DataProvider interface
  getList: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetListParams,
  ): Promise<GetListResult<RecordType>> => {
    const page = params.pagination?.page ?? 1;
    const perPage = params.pagination?.perPage ?? 25;
    const field = params.sort?.field ?? 'createdAt';
    const order = params.sort?.order ?? 'DESC';

    const query = new URLSearchParams();
    query.set('_page', page.toString());
    query.set('_perPage', perPage.toString());
    query.set('_sort', field);
    query.set('_order', order);

    if (params.filter) {
      Object.entries(params.filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query.set(key, String(value));
        }
      });
    }

    const url = `${apiUrl(resource)}?${query.toString()}`;
    const {data} = await httpClient(url);

    const items: RecordType[] = Array.isArray(data) ? data.map(addId) : [];

    return {
      data: items,
      total: items.length,
      pageInfo: {hasNextPage: false, hasPreviousPage: false},
    };
  },

  // biome-ignore lint/suspicious/noExplicitAny: Required by react-admin DataProvider interface
  getOne: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetOneParams,
  ): Promise<GetOneResult<RecordType>> => {
    const {data} = await httpClient(apiUrl(resource, String(params.id)));
    return {data: addId(data) as RecordType};
  },

  // biome-ignore lint/suspicious/noExplicitAny: Required by react-admin DataProvider interface
  getMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyParams,
  ): Promise<GetManyResult<RecordType>> => {
    const results = await Promise.all(params.ids.map((id) => httpClient(apiUrl(resource, String(id)))));
    return {
      data: results.map(({data}) => addId(data) as RecordType),
    };
  },

  // biome-ignore lint/suspicious/noExplicitAny: Required by react-admin DataProvider interface
  getManyReference: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<RecordType>> => {
    const page = params.pagination?.page ?? 1;
    const perPage = params.pagination?.perPage ?? 25;
    const field = params.sort?.field ?? 'createdAt';
    const order = params.sort?.order ?? 'DESC';

    const query = new URLSearchParams();
    query.set('_page', page.toString());
    query.set('_perPage', perPage.toString());
    query.set('_sort', field);
    query.set('_order', order);
    query.set(params.target, String(params.id));

    if (params.filter) {
      Object.entries(params.filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query.set(key, String(value));
        }
      });
    }

    const url = `${apiUrl(resource)}?${query.toString()}`;
    const {data} = await httpClient(url);

    const items: RecordType[] = Array.isArray(data) ? data.map(addId) : [];

    return {
      data: items,
      total: items.length,
      pageInfo: {hasNextPage: false, hasPreviousPage: false},
    };
  },

  // biome-ignore lint/suspicious/noExplicitAny: Required by react-admin DataProvider interface
  create: async <RecordType extends RaRecord = any>(
    resource: string,
    params: CreateParams,
  ): Promise<CreateResult<RecordType>> => {
    const {data} = await httpClient(apiUrl(resource), {
      method: 'POST',
      body: JSON.stringify(params.data),
    });
    return {data: addId(data) as RecordType};
  },

  // biome-ignore lint/suspicious/noExplicitAny: Required by react-admin DataProvider interface
  update: async <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateParams,
  ): Promise<UpdateResult<RecordType>> => {
    const {data} = await httpClient(apiUrl(resource, String(params.id)), {
      method: 'PATCH',
      body: JSON.stringify(params.data),
    });
    return {data: addId(data) as RecordType};
  },

  updateMany: async (resource: string, params: UpdateManyParams): Promise<UpdateManyResult> => {
    const results = await Promise.all(
      params.ids.map((id) =>
        httpClient(apiUrl(resource, String(id)), {
          method: 'PATCH',
          body: JSON.stringify(params.data),
        }),
      ),
    );
    return {data: results.map(({data}) => data.uuid)};
  },

  // biome-ignore lint/suspicious/noExplicitAny: Required by react-admin DataProvider interface
  delete: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteParams<RecordType>,
  ): Promise<DeleteResult<RecordType>> => {
    await httpClient(apiUrl(resource, String(params.id)), {
      method: 'DELETE',
    });
    return {data: params.previousData as RecordType};
  },

  deleteMany: async (resource: string, params: DeleteManyParams): Promise<DeleteManyResult> => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient(apiUrl(resource, String(id)), {
          method: 'DELETE',
        }),
      ),
    );
    return {data: params.ids};
  },
};

export default dataProvider;
